import { Button, Heading, Text, VStack } from "@chakra-ui/react"
import {
  CoverPageOfferFragment as CoverPageOffer,
  OfferPaymentType,
  OfferScope
} from "@pathwright/graphql-client/src/generated"
import { useState } from "react"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { canAdministrate, canEditLibrary } from "../user/permissions"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageTranslate } from "./utils"

const confirmButtonProps = {
  variant: "solid" as const,
  colorPalette: "brand",
  textDecoration: "none",
  fontWeight: "700",
  size: "xl" as const,
  w: "100%",
  maxW: "320px",
  _hover: { textDecoration: "none", opacity: 0.9 }
}

export function shouldConfirmStaffFreeOfferJoin({
  suppress,
  isLibraryEditorOrAdmin,
  cardInitiatedConvert,
  paymentType,
  offerScope
}: {
  suppress?: boolean
  isLibraryEditorOrAdmin: boolean
  cardInitiatedConvert: boolean
  paymentType?: string | null
  offerScope: OfferScope
}): boolean {
  if (suppress) return false
  if (!isLibraryEditorOrAdmin) return false
  if (!cardInitiatedConvert) return false
  if (offerScope !== OfferScope.Member) return false

  return paymentType?.toUpperCase() === OfferPaymentType.Free
}

type CoverPageOfferJoinConfirmationPanelProps = {
  staffRole: "Admin" | "Library Editor"
  pathName: string
  cohortName: string
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Confirmation before a free standard-cohort Offer registers immediately.
 * Shown to Admins / Library Editors who may be probing the Join CTA.
 */
export function CoverPageOfferJoinConfirmationPanel({
  staffRole,
  pathName,
  cohortName,
  onConfirm,
  onCancel
}: CoverPageOfferJoinConfirmationPanelProps) {
  const { t } = useCoverPageTranslate()
  const role = t("role_labels.noun.cohort_learner", {
    defaultValue: "learner"
  })

  return (
    <VStack
      className="CoverPageOfferJoinConfirmationGate"
      gap={4}
      w="full"
      align="center"
      textAlign="center"
      py={6}
      px={4}
    >
      <Heading as="h2" size="lg" m={0}>
        {t("offer.join_confirmation.title", {
          defaultValue: "Join as a {{role}}?",
          role
        })}
      </Heading>

      <Text m={0} maxW="360px">
        {t(
          staffRole === "Admin"
            ? "offer.join_confirmation.body_admin"
            : "offer.join_confirmation.body_library_editor",
          {
            defaultValue: `As ${
              staffRole === "Admin" ? "an" : "a"
            } ${staffRole}, you already have access to {{pathName}}. To confirm also joining {{cohortName}} as a {{role}}, select Join below.`,
            pathName,
            cohortName,
            role
          }
        )}
      </Text>

      <Button {...confirmButtonProps} onClick={onConfirm}>
        {t("offer.join_confirmation.confirm", { defaultValue: "Join" })}
      </Button>

      <Button
        variant="plain"
        size="sm"
        h="auto"
        minW="unset"
        p={0}
        fontSize="sm"
        fontWeight="normal"
        textDecoration="underline"
        color="inherit"
        onClick={onCancel}
      >
        {t("offer.join_confirmation.cancel", {
          defaultValue: "No, nevermind"
        })}
      </Button>
    </VStack>
  )
}

/**
 * Blocks mounting RegisterFlow for Admins / Library Editors until they confirm
 * joining a free standard-cohort Offer. Prevents accidental auto-registration
 * from Cover card `attemptToConvert`.
 *
 * Private Cohort Offers, paid Offers, learners, deep links, and thank-you /
 * order returns bypass this gate.
 */
export function OfferJoinConfirmationGate({
  offer,
  paymentType,
  suppress = false,
  children
}: {
  offer: CoverPageOffer
  paymentType?: string | null
  suppress?: boolean
  children: React.ReactNode
}) {
  const pwContext = usePathwrightContext()
  const resource = useCoverPageContext((v) => v.resource)
  const offerConversionIntentId = useCoverPageContext(
    (v) => v.offerConversionIntentId
  )
  const setOfferConversionIntentId = useCoverPageContext(
    (v) => v.setOfferConversionIntentId
  )
  const designPanelDisclosure = useCoverPageContext(
    (v) => v.designPanelDisclosure
  )
  const [confirmed, setConfirmed] = useState(false)

  const needsConfirmation = shouldConfirmStaffFreeOfferJoin({
    suppress,
    isLibraryEditorOrAdmin: canEditLibrary(pwContext),
    cardInitiatedConvert: offerConversionIntentId === offer.id,
    paymentType,
    offerScope: offer.scope
  })

  if (!needsConfirmation || confirmed) {
    return <>{children}</>
  }

  return (
    <CoverPageOfferJoinConfirmationPanel
      staffRole={canAdministrate(pwContext) ? "Admin" : "Library Editor"}
      pathName={resource?.name || "this Path"}
      cohortName={
        offer.cohort_name ? `the ${offer.cohort_name}` : "this Cohort"
      }
      onConfirm={() => setConfirmed(true)}
      onCancel={() => {
        setOfferConversionIntentId(null)
        designPanelDisclosure.onClose()
      }}
    />
  )
}

export default OfferJoinConfirmationGate
